import {useEffect, useEffectEvent, useMemo, useRef, useState, type SyntheticEvent} from 'react'

import type {PlayButtonAction} from '../../../brand/components/PlayButton/PlayButton'

export interface VideoPlaybackState {
  currentTime: number
  isPaused: boolean
  isPlaying: boolean
  isFinished: boolean
}

export interface VideoPlaybackStateSubscription {
  getState: () => Readonly<VideoPlaybackState> | null
  subscribe: (onStateChange: () => void) => () => void
}

interface UseVideoOptions {
  mediaUrl: string | undefined
  prefersReducedMotion: boolean
  threshold?: number
  preservePlaybackIntent?: boolean
  onActionChange?: (action: PlayButtonAction) => void
  onMetadata?: (media: HTMLVideoElement) => void
  onReady?: (media: HTMLVideoElement) => void
  acceptPlayback?: (media: HTMLVideoElement) => boolean
  onTimeUpdate?: (media: HTMLVideoElement) => void
  onReplay?: () => void
  onError?: (media: HTMLVideoElement) => void
}

const getPlaybackAction = (state: VideoPlaybackState | null): PlayButtonAction =>
  state?.isFinished ? 'replay' : state && !state.isPaused ? 'pause' : 'play'

/**
 * Owns the video element, playback events, and play/pause/replay requests, including visibility
 * autoplay and request errors. The component supplies its URL and reduced-motion preference.
 *
 * Stores the video position outside React and lets pagination read and subscribe directly.
 * Keeps the button action in React state and reports action changes to the parent when needed.
 * A sequence can reject media updates until its source is ready and preserve the user's playback
 * choice while restoring that source.
 */
export const useVideo = ({
  mediaUrl,
  prefersReducedMotion,
  threshold = 0.33,
  preservePlaybackIntent = false,
  onActionChange,
  onMetadata,
  onReady,
  acceptPlayback,
  onTimeUpdate,
  onReplay,
  onError,
}: UseVideoOptions) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const playbackStateRef = useRef<VideoPlaybackState | null>(null)
  const listenersRef = useRef(new Set<() => void>())
  const advancingRef = useRef(false)
  const autoplayHandledRef = useRef(false)

  const [isIntersecting, setIsIntersecting] = useState(false)
  const [playButtonAction, setPlayButtonAction] = useState<PlayButtonAction>('play')

  const playbackStateSubscription: VideoPlaybackStateSubscription = useMemo(
    () => ({
      getState: () => playbackStateRef.current,

      subscribe: onStateChange => {
        listenersRef.current.add(onStateChange)

        return () => {
          listenersRef.current.delete(onStateChange)
        }
      },
    }),
    [],
  )

  const publish = (state: VideoPlaybackState) => {
    // Media events can arrive before React renders the previous action change.
    const previousAction = getPlaybackAction(playbackStateRef.current)
    const nextAction = getPlaybackAction(state)
    playbackStateRef.current = state

    // Clock-only updates reach the animation subscribers without setting React state.
    if (nextAction !== previousAction) {
      setPlayButtonAction(nextAction)
      onActionChange?.(nextAction)
    }

    for (const listener of listenersRef.current) listener()
  }

  const updatePlayback = (media: HTMLVideoElement, eventType: string) => {
    // A replacement source must be validated and restored before its media state is used.
    if (acceptPlayback && !acceptPlayback(media)) return

    // A play request does not mean frames are advancing; buffering and seeking stop progress.
    if (eventType === 'playing') advancingRef.current = true
    if (['pause', 'waiting', 'seeking', 'ended', 'error', 'emptied'].includes(eventType)) advancingRef.current = false

    publish({
      currentTime: media.currentTime,
      isPaused: media.paused,
      isPlaying: advancingRef.current && !media.paused && !media.ended,
      isFinished: media.ended,
    })

    // Progress sees the new position before a chapter change replaces its animation.
    onTimeUpdate?.(media)
  }

  const reportPlayback = (event: SyntheticEvent<HTMLVideoElement>) => {
    if (preservePlaybackIntent && event.type === 'play') autoplayHandledRef.current = true

    updatePlayback(event.currentTarget, event.type)
  }

  const setPlayback = async (nextAction: PlayButtonAction, reason: 'manual' | 'automatic' = 'manual') => {
    const media = videoRef.current
    if (!media || (prefersReducedMotion && nextAction !== 'pause')) return

    const previous = playbackStateRef.current
    const recordsIntent = preservePlaybackIntent && reason === 'manual'

    // Native pause/time values reset when src changes; they cannot describe the user's choice.
    if (recordsIntent) {
      publish({
        currentTime: nextAction === 'replay' ? 0 : (previous?.currentTime ?? 0),
        isPaused: nextAction === 'pause',
        isPlaying: false,
        isFinished: false,
      })
    } else if (nextAction !== 'pause' || reason === 'manual') {
      autoplayHandledRef.current = true
    }

    if (nextAction === 'pause') {
      media.pause()
      advancingRef.current = false

      // Reduced motion may pause during source loading, when native time has reset.
      if (preservePlaybackIntent && !recordsIntent) {
        publish({
          currentTime: previous?.currentTime ?? 0,
          isPaused: true,
          isPlaying: false,
          isFinished: previous?.isFinished ?? media.ended,
        })
      }

      return
    }

    if (nextAction === 'replay') {
      media.currentTime = 0
      onReplay?.()
    }

    const source = media.getAttribute('src')

    try {
      const playing = media.play()
      if (preservePlaybackIntent && !recordsIntent) updatePlayback(media, 'play')

      await playing
    } catch (error) {
      const interrupted = error instanceof DOMException && error.name === 'AbortError'

      // A rejected request for an old source must not overwrite the replacement's state.
      if (media.getAttribute('src') === source) {
        if (preservePlaybackIntent && !recordsIntent) updatePlayback(media, 'error')
        else if (recordsIntent && !interrupted && videoRef.current === media) {
          publish({
            currentTime: playbackStateRef.current?.currentTime ?? 0,
            isPaused: true,
            isPlaying: false,
            isFinished: playbackStateRef.current?.isFinished ?? false,
          })
        }
      }

      // Interrupted user requests retain intent; blocked automatic requests are not failures to report.
      if (
        interrupted ||
        (reason === 'automatic' && error instanceof DOMException && error.name === 'NotAllowedError')
      ) {
        return
      }

      window.reportError(error)
    }
  }

  const togglePlay = () => {
    const media = videoRef.current
    if (!media) return

    const nextAction = preservePlaybackIntent
      ? getPlaybackAction(playbackStateRef.current)
      : media.ended
        ? 'replay'
        : media.paused
          ? 'play'
          : 'pause'

    return setPlayback(nextAction)
  }

  const autoplay = () => {
    const media = videoRef.current

    if (
      !media ||
      !isIntersecting ||
      prefersReducedMotion ||
      autoplayHandledRef.current ||
      media.getAttribute('src') !== mediaUrl ||
      (acceptPlayback && !acceptPlayback(media))
    ) {
      return
    }

    void setPlayback('play', 'automatic')
  }

  const handleError = (event: SyntheticEvent<HTMLVideoElement>) => {
    const media = event.currentTarget
    media.pause()
    advancingRef.current = false

    publish({
      currentTime: playbackStateRef.current?.currentTime ?? 0,
      isPaused: true,
      isPlaying: false,
      isFinished: false,
    })

    if (onError) onError(media)
    else window.reportError(new Error(`Video failed to load (media error ${media.error?.code}).`))
  }

  const applyReducedMotion = useEffectEvent(() => {
    if (prefersReducedMotion) void setPlayback('pause', 'automatic')
  })

  useEffect(() => {
    applyReducedMotion()
  }, [prefersReducedMotion])

  useEffect(() => {
    advancingRef.current = false
  }, [mediaUrl])

  const reportMetadata = useEffectEvent((media: HTMLVideoElement) => onMetadata?.(media))
  const reportReady = useEffectEvent((media: HTMLVideoElement) => onReady?.(media))

  useEffect(() => {
    const media = videoRef.current
    if (!media) return

    const handleMetadata = () => {
      if (media.readyState >= media.HAVE_METADATA && media.getAttribute('src') === mediaUrl) {
        reportMetadata(media)
      }
    }
    const handleCanPlay = () => {
      if (media.readyState >= media.HAVE_FUTURE_DATA && media.getAttribute('src') === mediaUrl) {
        reportReady(media)
      }
    }

    media.addEventListener('loadedmetadata', handleMetadata)
    media.addEventListener('canplay', handleCanPlay)

    // Cached metadata may trigger a seek, so check playback readiness after initialization.
    handleMetadata()
    handleCanPlay()

    return () => {
      media.removeEventListener('loadedmetadata', handleMetadata)
      media.removeEventListener('canplay', handleCanPlay)
    }
  }, [mediaUrl])

  useEffect(() => {
    const media = videoRef.current
    if (!media) return

    // Delay autoplay until the video first enters the viewport.
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsIntersecting(true)
          observer.disconnect()
        }
      },
      {threshold},
    )

    observer.observe(media)

    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaUrl])

  const syncAutoplay = useEffectEvent(autoplay)

  useEffect(() => {
    syncAutoplay()
  }, [isIntersecting, prefersReducedMotion, mediaUrl])

  return {
    videoRef,
    playbackStateSubscription,
    playButtonAction,
    setPlayback,
    togglePlay,
    reportPlayback,
    handleError,
    updatePlayback,
    autoplay,
  }
}
